import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Icon, NonIdealState, Spinner, Tag } from '@blueprintjs/core';
import { useQueryClient } from '@tanstack/react-query';
import { toast as message } from 'sonner';
import {
  DRIVE_FOLDER_MIME_TYPE,
  GoogleDriveError,
  canonicalDriveFolderUrl,
  drivePreviewUrl,
  extractDriveFolderId,
  formatDriveDate,
  listDriveFilesComplete,
  uploadDriveFile,
  type DriveFile,
} from './drive';
import { connectGoogleDrive as connectGoogleDriveShared } from './googleToken';
import { driveFilesQueryKey, useDriveFiles } from './useDriveFiles';
import { useFileViewer } from '../../components/FileViewer';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { invalidateAndRefreshAllQueriesForTable } from '../../utils/metadata';
import { handleOperationError } from '../../utils/supabase-operations';
import { supabaseClient } from '../../utils/supabase';
import useAuth from '../../utils/useAuth';

interface DriveFileListProps {
  merchantId: number;
  driveFolder: string | null | undefined;
  driveFolderLoading: boolean;
  providerToken: string | null;
}

const RECURSIVE_PREFETCH_MAX_DEPTH = 6;
const RECURSIVE_PREFETCH_MAX_ITEMS = 3000;

export function DriveFileList({ merchantId, driveFolder, driveFolderLoading, providerToken }: DriveFileListProps) {
  const queryClient = useQueryClient();
  const { openFile: openFileViewer } = useFileViewer();
  const { session } = useAuth();
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: string; name: string }>>([]);
  // previewFile state removed — files now open in full-screen FileViewer
  const [dropFolderId, setDropFolderId] = useState<string | null>(null);
  const [uploadingToFolderId, setUploadingToFolderId] = useState<string | null>(null);
  const [uncachedFolderIds, setUncachedFolderIds] = useState<Set<string>>(() => new Set());
  const recursivePrefetchStartedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: driveFiles = [], isLoading: driveFilesLoading, error: driveError, rootFolderId, currentFolderId } =
    useDriveFiles(driveFolder, activeFolderId, providerToken);

  useEffect(() => {
    if (currentFolderId && !driveFilesLoading && !driveError) {
      setUncachedFolderIds((current) => {
        if (!current.has(currentFolderId)) return current;
        const next = new Set(current);
        next.delete(currentFolderId);
        return next;
      });
    }
  }, [currentFolderId, driveError, driveFilesLoading]);

  useEffect(() => {
    recursivePrefetchStartedRef.current = false;
    setUncachedFolderIds(new Set());
  }, [providerToken, rootFolderId]);

  // A merchant's folder can be changed from the side-panel header while this
  // list is expanded. The old child folder must not remain selected under the
  // new root, otherwise the browser would keep showing stale files.
  useEffect(() => {
    setActiveFolderId(null);
    setBreadcrumbs([]);
    setDropFolderId(null);
  }, [rootFolderId]);

  const driveErrorMessage =
    driveError instanceof Error ? driveError.message : driveError ? String(driveError) : '';
  const driveAccountEmail = session?.user.email;
  const driveFolderUrl = canonicalDriveFolderUrl(driveFolder);
  const canReconnect =
    !providerToken ||
    (driveError instanceof GoogleDriveError && ['auth', 'access', 'not_found'].includes(driveError.code));

  const markFolderUncached = (folderId: string) => {
    setUncachedFolderIds((current) => {
      if (current.has(folderId)) return current;
      const next = new Set(current);
      next.add(folderId);
      return next;
    });
    queryClient.removeQueries({ queryKey: driveFilesQueryKey(folderId, providerToken), exact: true });
  };

  const clearFolderUncached = (folderId: string) => {
    setUncachedFolderIds((current) => {
      if (!current.has(folderId)) return current;
      const next = new Set(current);
      next.delete(folderId);
      return next;
    });
  };

  const prefetchDriveTree = async (folderId: string, depth: number, budget: { remaining: number }) => {
    if (!providerToken || depth > RECURSIVE_PREFETCH_MAX_DEPTH || budget.remaining <= 0) {
      markFolderUncached(folderId);
      return;
    }

    const result = await listDriveFilesComplete(folderId, providerToken, {
      maxItems: budget.remaining,
      // Child folders came from a visible parent listing. Avoid a metadata
      // request per folder while warming the recursive cache.
      verifyFolder: depth === 0,
    });
    if (!result.complete || result.files.length > budget.remaining) {
      markFolderUncached(folderId);
      return;
    }

    budget.remaining -= result.files.length;
    clearFolderUncached(folderId);
    queryClient.setQueryData<DriveFile[]>(driveFilesQueryKey(folderId, providerToken), result.files);

    const childFolders = result.files.filter((file) => file.mimeType === DRIVE_FOLDER_MIME_TYPE);
    for (const childFolder of childFolders) {
      await prefetchDriveTree(childFolder.id, depth + 1, budget);
      if (budget.remaining <= 0) {
        for (const skippedFolder of childFolders.slice(childFolders.indexOf(childFolder) + 1)) {
          markFolderUncached(skippedFolder.id);
        }
        break;
      }
    }
  };

  const startRecursivePrefetch = (folderId: string) => {
    if (!providerToken || recursivePrefetchStartedRef.current) return;
    recursivePrefetchStartedRef.current = true;
    void prefetchDriveTree(folderId, 0, { remaining: RECURSIVE_PREFETCH_MAX_ITEMS }).catch((error) => {
      console.warn('Google Drive recursive prefetch failed:', error);
    });
  };

  // Shared connect flow (Drive scope + account chooser) for stale sessions or
  // a Google account that cannot access this merchant's folder.
  const connectGoogleDrive = connectGoogleDriveShared;

  const handleOpenFolder = (file: DriveFile) => {
    startRecursivePrefetch(rootFolderId ?? file.id);
    if (uncachedFolderIds.has(file.id)) {
      queryClient.removeQueries({ queryKey: driveFilesQueryKey(file.id, providerToken), exact: true });
    }
    setActiveFolderId(file.id);
    void queryClient.invalidateQueries({ queryKey: driveFilesQueryKey(file.id, providerToken), exact: true });
    setBreadcrumbs((prev) => [...prev, { id: file.id, name: file.name }]);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) {
      if (rootFolderId) {
        void queryClient.invalidateQueries({ queryKey: driveFilesQueryKey(rootFolderId, providerToken), exact: true });
      }
      setActiveFolderId(null);
      setBreadcrumbs([]);
      return;
    }
    const crumb = breadcrumbs[index];
    if (uncachedFolderIds.has(crumb.id)) {
      queryClient.removeQueries({ queryKey: driveFilesQueryKey(crumb.id, providerToken), exact: true });
    }
    setActiveFolderId(crumb.id);
    void queryClient.invalidateQueries({ queryKey: driveFilesQueryKey(crumb.id, providerToken), exact: true });
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  const uploadFilesToFolder = async (files: FileList | File[], folderId: string, folderName: string) => {
    if (!providerToken || files.length === 0) return;
    const fileArray = Array.from(files);
    const queryKey = driveFilesQueryKey(folderId, providerToken);
    const insertAfterFolders = (newFiles: DriveFile[], existing: DriveFile[] = []) => {
      const newFileIds = new Set(newFiles.map((file) => file.id));
      const folders = existing.filter((file) => file.mimeType === DRIVE_FOLDER_MIME_TYPE && !newFileIds.has(file.id));
      const regularFiles = existing.filter((file) => file.mimeType !== DRIVE_FOLDER_MIME_TYPE && !newFileIds.has(file.id));
      return [...folders, ...newFiles, ...regularFiles];
    };
    const optimisticFiles: DriveFile[] = fileArray.map((file) => ({
      id: `uploading-${folderId}-${file.name}-${file.lastModified}-${file.size}`,
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      modifiedTime: new Date().toISOString(),
    }));
    setUploadingToFolderId(folderId);
    queryClient.setQueryData<DriveFile[]>(queryKey, (existing = []) => {
      const existingIds = new Set(existing.map((file) => file.id));
      return insertAfterFolders(
        optimisticFiles.filter((file) => !existingIds.has(file.id)),
        existing,
      );
    });
    const toastId = message.loading(
      `Uploading ${fileArray.length} file${fileArray.length === 1 ? '' : 's'} to ${folderName}...`,
    );
    try {
      for (let index = 0; index < fileArray.length; index += 1) {
        const uploadedFile = await uploadDriveFile(folderId, fileArray[index], providerToken);
        const optimisticId = optimisticFiles[index].id;
        queryClient.setQueryData<DriveFile[]>(queryKey, (existing = []) =>
          insertAfterFolders(
            [uploadedFile],
            existing.filter((file) => file.id !== optimisticId),
          ),
        );
      }
      void queryClient.invalidateQueries({ queryKey });
      message.success(`Uploaded ${fileArray.length} file${fileArray.length === 1 ? '' : 's'} to ${folderName}.`, {
        id: toastId,
      });
    } catch (error) {
      const optimisticIds = new Set(optimisticFiles.map((file) => file.id));
      queryClient.setQueryData<DriveFile[]>(queryKey, (existing = []) =>
        existing.filter((file) => !optimisticIds.has(file.id)),
      );
      const errorMessage = error instanceof Error ? error.message : String(error);
      message.error(errorMessage, { id: toastId });
      void queryClient.invalidateQueries({ queryKey });
    } finally {
      setUploadingToFolderId(null);
      setDropFolderId(null);
    }
  };

  const handleDriveDragOver = (event: React.DragEvent, folderId: string) => {
    if (!providerToken || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setDropFolderId(folderId);
  };

  const handleDriveDragLeave = (event: React.DragEvent, folderId: string) => {
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setDropFolderId((current) => (current === folderId ? null : current));
    }
  };

  const handleDriveDrop = (
    event: React.DragEvent,
    folderId: string | null | undefined,
    folderName: string,
  ) => {
    if (!providerToken || !folderId) return;
    event.preventDefault();
    event.stopPropagation();
    void uploadFilesToFolder(event.dataTransfer.files, folderId, folderName);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length || !currentFolderId) return;
    void uploadFilesToFolder(files, currentFolderId, breadcrumbs[breadcrumbs.length - 1]?.name ?? 'current folder');
    event.target.value = '';
  };

  const currentFolderName = breadcrumbs[breadcrumbs.length - 1]?.name ?? 'current folder';
  const dropTargetName = dropFolderId === currentFolderId
    ? currentFolderName
    : driveFiles.find((file) => file.id === dropFolderId)?.name;
  const isUploading = uploadingToFolderId !== null;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <div className="sky-merchant-docs-drive-head">
        <div className="sky-merchant-docs-toolbar">
          {canReconnect ? (
            <Button
              small
              intent="primary"
              text={providerToken ? 'Reconnect Google Drive' : 'Connect Google Drive'}
              onClick={() => void connectGoogleDrive()}
            />
          ) : (
            <Tag minimal>{driveFiles.length} items</Tag>
          )}
          <div className="sky-merchant-docs-toolbar-actions">
            {!canReconnect && (
              <Tag minimal icon="upload" interactive onClick={() => fileInputRef.current?.click()} style={{ cursor: 'pointer' }}>
                Upload files
              </Tag>
            )}
            <DriveFolderLinkControl merchantId={merchantId} driveFolder={driveFolder} />
          </div>
        </div>
      </div>

      {driveFolderLoading ? (
        <div className="sky-merchant-docs-loading">
          <Spinner size={18} />
        </div>
      ) : !driveFolder || !rootFolderId ? (
        <div className="sky-merchant-docs-empty-card">
          No Google Drive folder linked on this merchant.
        </div>
      ) : !providerToken ? (
        <NonIdealState
          icon="key"
          title="Google Drive permission needed"
          description="Connect Google Drive to browse and preview this merchant's folder."
        />
      ) : driveError ? (
        <NonIdealState
          icon="error"
          title="Could not load Drive files"
          description={`${driveErrorMessage}${driveAccountEmail ? ` Signed in as ${driveAccountEmail}.` : ''}`}
          action={
            canReconnect ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button intent="primary" text="Use another Google account" onClick={() => void connectGoogleDrive()} />
                {driveFolderUrl && (
                  <Button
                    text="Open in Drive"
                    onClick={() => window.open(driveFolderUrl, '_blank', 'noopener,noreferrer')}
                  />
                )}
              </div>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="sky-merchant-docs-breadcrumbs">
            <button
              type="button"
              className="sky-merchant-docs-crumb"
              onClick={() => handleBreadcrumbClick(-1)}
            >
              Root
            </button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.id}>
                <span className="sky-merchant-docs-crumb-sep">/</span>
                <button
                  type="button"
                  className="sky-merchant-docs-crumb"
                  onClick={() => handleBreadcrumbClick(index)}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {driveFilesLoading ? (
            <div className="sky-merchant-docs-loading">
              <Spinner size={18} />
            </div>
          ) : driveFiles.length === 0 ? (
            <div
              className={`sky-merchant-docs-empty-card sky-merchant-docs-upload-target${dropFolderId === currentFolderId ? ' is-drop-target' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => handleDriveDragOver(event, currentFolderId as string)}
              onDragLeave={(event) => handleDriveDragLeave(event, currentFolderId as string)}
              onDrop={(event) => handleDriveDrop(event, currentFolderId, currentFolderName)}
            >
              {dropFolderId === currentFolderId && (
                <div className="sky-merchant-docs-drop-label">Upload to {currentFolderName}</div>
              )}
              Drive folder is connected, but no files were found here. Drop or click here to upload files.
            </div>
          ) : (
            <div
              className={`sky-merchant-docs-layout${dropFolderId === currentFolderId ? ' is-drop-target' : ''}`}
              onDragOver={(event) => handleDriveDragOver(event, currentFolderId as string)}
              onDragLeave={(event) => handleDriveDragLeave(event, currentFolderId as string)}
              onDrop={(event) => handleDriveDrop(event, currentFolderId, currentFolderName)}
            >
              {dropTargetName && (
                <div className="sky-merchant-docs-drop-label">Upload to {dropTargetName}</div>
              )}
              <div className="sky-merchant-docs-list">
                {driveFiles.map((file) => {
                  const isFolder = file.mimeType === DRIVE_FOLDER_MIME_TYPE;
                  return (
                    <div
                      key={file.id}
                      className={[
                        'sky-merchant-docs-row',
                        isFolder ? 'sky-merchant-docs-row--folder' : '',
                        dropFolderId === file.id ? 'is-drop-target' : '',
                      ].filter(Boolean).join(' ')}
                      onDragOver={isFolder ? (event) => handleDriveDragOver(event, file.id) : undefined}
                      onDragLeave={isFolder ? (event) => handleDriveDragLeave(event, file.id) : undefined}
                      onDrop={isFolder ? (event) => handleDriveDrop(event, file.id, file.name) : undefined}
                    >
                      <button
                        type="button"
                        className="sky-merchant-docs-preview-button"
                        disabled={isUploading}
                        onClick={() => {
                          if (isFolder) handleOpenFolder(file);
                          else openFileViewer({ directUrl: drivePreviewUrl(file), filename: file.name, mimeType: file.mimeType ?? undefined });
                        }}
                      >
                        {file.iconLink ? (
                          <img alt="" src={file.iconLink} className="sky-merchant-docs-icon" />
                        ) : (
                          <span className="sky-merchant-docs-icon-placeholder">
                            {isFolder ? 'DIR' : 'FILE'}
                          </span>
                        )}
                        <span className="sky-merchant-docs-name">{file.name}</span>
                        <span className="sky-merchant-docs-date">
                          {uploadingToFolderId === file.id
                            ? 'Uploading...'
                            : dropFolderId === file.id
                              ? `Upload to ${file.name}`
                              : isFolder
                                ? 'Folder - drop to upload'
                                : formatDriveDate(file.modifiedTime)}
                        </span>
                      </button>
                      <Button
                        minimal
                        small
                        icon="share"
                        disabled={isUploading}
                        aria-label={`Open ${file.name} in Google Drive`}
                        onClick={() =>
                          window.open(
                            file.webViewLink ?? `https://drive.google.com/file/d/${file.id}/view`,
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function DriveFolderLinkControl({
  merchantId,
  driveFolder,
}: {
  merchantId: number;
  driveFolder: string | null | undefined;
}) {
  const queryClient = useQueryClient();
  const { hasCapability } = useMyCapabilities();
  const canEdit = hasCapability('edit_deals');
  const savedFolderUrl = canonicalDriveFolderUrl(driveFolder);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(driveFolder ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // This toolbar stays mounted while the side panel changes merchants. Reset
  // its hidden state so a prior merchant's URL can never appear in this menu.
  useEffect(() => {
    setOpen(false);
    setEditing(false);
    setDraft(driveFolder ?? '');
    setError(null);
  }, [merchantId, driveFolder]);

  const close = useCallback(() => {
    setOpen(false);
    setEditing(false);
    setDraft(driveFolder ?? '');
    setError(null);
  }, [driveFolder]);

  const handleCopy = useCallback(async () => {
    if (!savedFolderUrl) return;
    try {
      await navigator.clipboard.writeText(savedFolderUrl);
      message.success('Drive link copied');
    } catch {
      message.error('Could not copy the Drive link');
    }
  }, [savedFolderUrl]);

  const save = useCallback(async () => {
    const value = draft.trim();
    const nextFolderUrl = value ? canonicalDriveFolderUrl(value) : null;
    if (value && !nextFolderUrl) {
      setError('Enter a Google Drive folder URL or folder ID.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { data, error: updateError } = await supabaseClient
        .from('merchant')
        .update({ drive_folder: nextFolderUrl })
        .eq('id', merchantId)
        .select('id')
        .maybeSingle();
      if (updateError) throw updateError;
      if (!data) throw new Error('The merchant could not be updated.');

      const folderId = extractDriveFolderId(nextFolderUrl);
      await Promise.all([
        invalidateAndRefreshAllQueriesForTable('merchant'),
        queryClient.invalidateQueries({
          queryKey: ['docs-drive-folder', 'merchant', merchantId],
          exact: true,
        }),
        folderId
          ? queryClient.invalidateQueries({ queryKey: ['docs-drive-files', folderId] })
          : Promise.resolve(),
      ]);
      setDraft(nextFolderUrl ?? '');
      setEditing(false);
      setOpen(false);
      message.success(nextFolderUrl ? 'Drive folder saved' : 'Drive folder cleared');
    } catch (saveError) {
      const info = handleOperationError(saveError, 'Failed to save Drive folder');
      setError(info.userMessage);
      message.error(info.userMessage);
    } finally {
      setSaving(false);
    }
  }, [draft, merchantId, queryClient]);

  if (!savedFolderUrl && !canEdit) return null;

  return (
    <div className="sky-merchant-docs-link-control">
      {savedFolderUrl && (
        <button
          type="button"
          className="sky-merchant-docs-link-action"
          onClick={() => {
            setOpen(true);
            setEditing(false);
          }}
        >
          Drive link
        </button>
      )}
      {savedFolderUrl && (
        <button
          type="button"
          className="sky-merchant-docs-link-copy"
          onClick={() => void handleCopy()}
          title="Copy Drive folder link"
          aria-label="Copy Drive folder link"
        >
          <Icon icon="duplicate" size={15} aria-hidden="true" />
        </button>
      )}
      {canEdit && !savedFolderUrl && (
        <button
          type="button"
          className="sky-merchant-docs-link-action"
          onClick={() => {
            setOpen(true);
            setEditing(true);
          }}
        >
          Add Drive link
        </button>
      )}
      {open && (
        <form
          className="sky-merchant-docs-link-menu"
          onSubmit={(event) => {
            event.preventDefault();
            if (!saving) void save();
          }}
        >
          <div className="sky-merchant-docs-link-menu-head">
            <label htmlFor={`merchant-drive-folder-${merchantId}`}>Drive folder link</label>
            {canEdit && !editing && (
              <button type="button" className="sky-merchant-docs-link-action" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
          </div>
          <input
            id={`merchant-drive-folder-${merchantId}`}
            className="sky-merchant-docs-link-input"
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Paste a Drive folder URL or ID"
            readOnly={!editing}
            disabled={saving}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `merchant-drive-folder-error-${merchantId}` : undefined}
            autoFocus={editing}
          />
          {error && <div id={`merchant-drive-folder-error-${merchantId}`} className="sky-merchant-docs-link-error" role="alert">{error}</div>}
          <div className="sky-merchant-docs-link-menu-actions">
            {editing && savedFolderUrl && draft && (
              <button type="button" className="sky-merchant-docs-link-action" onClick={() => setDraft('')} disabled={saving}>
                Clear
              </button>
            )}
            <button type="button" className="sky-merchant-docs-link-action" onClick={close} disabled={saving}>
              {editing ? 'Cancel' : 'Close'}
            </button>
            {editing && (
              <button type="submit" className="sky-merchant-docs-link-save" disabled={saving}>
                {saving ? 'Saving…' : draft.trim() ? 'Save' : 'Clear link'}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
